import { useEffect, useRef, useState } from 'react';

/** Where one of the home screen's numbers stands. */
export type Read<T> =
  /** Not asked: the person does not hold it, or no project is selected. */
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'ready'; value: T }
  /** Refused, failed, not known yet, or the answer did not carry it. The screen leaves it out. */
  | { status: 'failed' };

export const OFF: Read<never> = { status: 'off' };
export const LOADING: Read<never> = { status: 'loading' };
export const FAILED: Read<never> = { status: 'failed' };

/** The value once it has arrived, otherwise null. */
export const valueOf = <T,>(read: Read<T>): T | null => (read.status === 'ready' ? read.value : null);

/**
 * What a loader answers when the server holds no number YET and has started reading
 * one: the three numbers it keeps in the background, on the first open after a quiet
 * spell or a deploy. It is drawn as any number that is not known, and asked for again.
 */
export const NOT_KNOWN_YET: unique symbol = Symbol('not known yet');
export type NotKnownYet = typeof NOT_KNOWN_YET;

// The waits before each further ask: about 20 seconds, 1, 2, 4 and 10 minutes after the
// first answer. The server reads ONE number at a time for every project there is, and a
// whole-project read takes from a few seconds (stores short of people) to several
// minutes (a month of requests on the largest projects), so a number asked for behind
// others lands minutes later. Asking only in the first minute left those numbers off
// the screen for the whole visit although the server had them a little later.
//
// The last ask is the one that must catch the slowest number. On the server a refresh
// waits in line for at most 3 minutes before it goes ahead of quicker ones
// (LINE_PATIENCE_MS in apps/api/src/services/home-kept-counts.store.ts), then the read
// already running has to finish, then its own read runs. 10 minutes leaves room for all
// three; change the two together.
//
// While nothing is kept an ask costs the server nothing upstream (it does not work out
// whose number it is until there is one), and asking stops at the first number. Five
// and no more, slower and slower: this is a landing page, not a poll.
const ASK_AGAIN_AFTER_MS = [20_000, 40_000, 60_000, 120_000, 360_000];

/**
 * Ask once per `key`, and keep the answer WITH the key it was asked under.
 *
 * The key names everything the answer depends on (the project, the month, the roles).
 * When it changes, the old answer stops being shown in the same render, before the new
 * request has even started, and an answer that arrives late for an old key is dropped.
 * So a number counted for one project is never drawn under another.
 *
 * A null key asks nothing. Each number has its own read, so a slow or refused call
 * holds back only itself.
 *
 * The one case asked more than once is a loader that answers NOT_KNOWN_YET. It reads as
 * failed meanwhile (never as loading again, so nothing on screen flickers), and is asked
 * five more times at most, only after such an answer and only while the tab is in view: an
 * ask that falls due in a hidden tab waits for the person to come back. A number that
 * arrives replaces the failure and ends it; so does a call that fails. Leaving the
 * screen or changing the key drops whatever was still to come.
 */
export function useRead<T>(key: string | null, load: () => Promise<T | NotKnownYet>): Read<T> {
  const [held, setHeld] = useState<{ key: string; read: Read<T> } | null>(null);
  // Latest loader in a ref so a new function identity never asks again: only the key does.
  const loadRef = useRef(load);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    if (key === null) return undefined;
    let current = true;
    let askedAgain = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let waitingForView = false;

    const ask = () => {
      Promise.resolve()
        .then(() => loadRef.current())
        .then(
          (value) => {
            if (!current) return;
            if (value !== NOT_KNOWN_YET) { setHeld({ key, read: { status: 'ready', value } }); return; }
            setHeld({ key, read: FAILED });
            if (askedAgain < ASK_AGAIN_AFTER_MS.length) timer = setTimeout(askAgainInView, ASK_AGAIN_AFTER_MS[askedAgain]);
          },
          () => { if (current) setHeld({ key, read: FAILED }); },
        );
    };

    const askAgain = () => { askedAgain += 1; ask(); };
    const onViewChange = () => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', onViewChange);
      waitingForView = false;
      askAgain();
    };
    function askAgainInView() {
      timer = null;
      if (document.visibilityState === 'visible') { askAgain(); return; }
      waitingForView = true;
      document.addEventListener('visibilitychange', onViewChange);
    }

    ask();
    return () => {
      current = false;
      if (timer !== null) clearTimeout(timer);
      if (waitingForView) document.removeEventListener('visibilitychange', onViewChange);
    };
  }, [key]);

  if (key === null) return OFF;
  return held && held.key === key ? held.read : LOADING;
}
